import { MessageContext, getMessageGetter } from "@messageformat/react";
import { get, isBoolean, isNumber, isString } from "lodash";
import { useCallback, useContext, useMemo } from "react";

// Return type of the message getter function
export type MessageGetter = {
  (id?: string, params?: Record<string, unknown>): string;
  has: (id: string) => boolean;
};

/**
 * A wrapper around useMessageGetter that ensures the returned function always returns a string or throws an error.
 * This eliminates the need for manual type casting and provides better runtime safety.
 * Uses native ICU MessageFormat for variable interpolation.
 * @param args - All parameters passed to useMessageGetter (spread operator)
 * @returns A function that takes a translation key and optional variables object, returns the translated string with variables replaced
 * @throws {Error} If the translation is missing or not a string
 * @example
 * messages.yaml:
 * component:
 *   title: "Hello, {name}!"
 *
 * react_component.tsx:
 * const t = useTranslator("myComponent");
 * const title = t("component.title", { name: "John" }); // "Hello, John!"
 */
export function useTranslator(rootId: string): MessageGetter {
  /**
   * NOTE: We cannot use useMessageGetter directly because it is unstable. That is, it
   * returns a new function instance on every render, causing unnecessary re-renders
   * in components that use the translator function.
   */
  const messageContext = useContext(MessageContext);
  const messageGetter = useMemo(
    () => getMessageGetter(messageContext, rootId),
    [messageContext, rootId],
  );

  const translator = useCallback(
    (id?: string, params?: Record<string, unknown>): string => {
      if (id === "" || id === undefined) {
        console.warn("Empty or undefined translation key provided");
        return "";
      }

      // Pass variables directly to messageGetter for ICU MessageFormat interpolation
      const result: unknown = messageGetter(id, params);

      if (result === null || result === undefined) {
        throw new Error(`Translation missing for key: "${id}"`);
      }

      // Handle the result type - MessageFormat returns strings for parameterized messages
      if (isString(result)) {
        return result;
      } else if (isNumber(result) || isBoolean(result)) {
        return String(result);
      } else {
        throw new Error(
          `Translation for key "${id}" is not a string, got: ${typeof result}`,
        );
      }
    },
    [messageGetter],
  );

  const has = useCallback(
    (id: string) => {
      if (!messageContext) return false;
      const fullPath = rootId ? `${rootId}.${id}` : id;

      // Handle case where messages are nested under an empty string key
      const messages =
        get(messageContext.messages, [""]) || messageContext.messages;
      const value: unknown = get(messages, fullPath);

      return value !== undefined && value !== null;
    },
    [messageContext, rootId],
  );

  return Object.assign(translator, { has });
}
